import { AppBar, Box, Button, Grid, IconButton, Typography, useTheme } from "@mui/material";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { NavLink, useHistory } from "react-router-dom";
import BackIcon from "src/icons/BackIcon";
import Hamburguer from "src/icons/Hamburguer";

// component
import { useAppDispatch } from "src/store/store";
import NotificationAddIcon from "@mui/icons-material/NotificationAdd";
import { trackEvent } from "src/Amplitude";
import CalebAndBrownLogo from "src/icons/cnbDesktop";
import { togglePriceAlertModal } from "src/store/features/PriceAlerts.slice";
import api from "src/store/features/api";
import { useGetConfigQuery, useGetWatchlistV2Query } from "src/store/features/api/generated";
import { setMaintenanceStatus } from "src/store/features/maintenance.slice";
import { useMarketsState } from "src/store/features/markets.slice";
import useHydrateUser from "src/utils/hooks/hydrateUser";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import useIsUkUserCountry from "src/utils/hooks/useIsUkUserCountry";
import ContainedButton from "../common/ContainedButton";
import { MarketTableStyles } from "../markets/Table/styles/MarketTable.styles";
import WatchToggle from "../markets/WatchToggle";
import ProfileModal from "../multi-account/ProfileModal";
import RaFInviteModal from "../raf/Invite/RaFInviteModal";
import { tabsType } from "../settings/profile/Profile";
import ProfileComponent, { BrokerModal } from "./BrokerHumanisation";
import LogoutToUpdateModal from "./LogoutToUpdateModal";
import CollapsibleGlobalSearchbar from "./CollapsibleGlobalSearchbar";
import {
  DesktopAppbar,
  DesktopAppbarButtons,
  DesktopAppbarComponents,
  HeaderStyles,
} from "./styles/Header.styles";
import {
  RafTiers,
  setAccountLock,
  setUserRaf,
  setWatchlist,
  useUserSlice,
} from "src/store/features/user.slice";
import usePermission from "src/utils/hooks/usePermission";

interface HeaderProps {
  open: boolean;
  back?: boolean;
  title?: string;
  eye?: boolean;
  handleMenuOpen: () => void;
  toggleTheme?: () => void;
  useDefaultTheme?: boolean;
  hideRightIcon?: boolean;
  hideFavourites?: boolean;
  displayTabs?: boolean;
  setSelectedTab?: Dispatch<SetStateAction<tabsType>>;
}

const Header = ({ handleMenuOpen, back, title, hideRightIcon, hideFavourites }: HeaderProps) => {
  //------------- Auto conversion logic-------------
  const [logoutToUpdateModal, setLogoutToUpdateModal] = useState(false);

  // Get permission
  const { userPermission } = usePermission();
  const isGuest = userPermission === "guest";

  // If user is a guest refetch every 30 seconds. Otherwise Skip
  const { data: kycStatusRespone } = api.useGetKycStatusQuery(undefined, {
    pollingInterval: isGuest ? 30000 : undefined,
    skip: !isGuest,
  });

  useEffect(() => {
    if (kycStatusRespone?.isVerified && isGuest) {
      // Tell the user to logout to update their account
      setLogoutToUpdateModal(true);
    }
  }, [kycStatusRespone, isGuest]);

  //------------- USE STATES-------------
  const [open, setOpen] = React.useState(false);
  const [InviteModalOpen, setInviteModalOpen] = React.useState<boolean>(false);

  //------------- API -------------
  const { data: userData } = api.useGetUserDatav2Query();
  const isUkUser = useIsUkUserCountry();
  // refetch maintenance state every 5 minutes
  const { data } = useGetConfigQuery(undefined, { pollingInterval: 300000 });
  // get user RaF data
  const { data: rafData, isLoading: isLoadingRaf } = api.useGetReferralsQuery(undefined, {
    skip: isUkUser,
  });

  //------------- REDUX -------------
  const dispatchRedux = useAppDispatch();
  const { coinStatistics } = useMarketsState();
  useHydrateUser({ userData, checkFirstTimeLogin: true }); // Hydrate user data every minute
  const {
    isUnverified,
    raf: { OLD_RAF_SYSTEM, rafOptedIn },
    watchlist: currentWatchlist,
  } = useUserSlice();

  // get watchlist data for initial load (v2)
  // Only fetch if watchlist is empty to avoid overwriting optimistic updates or recent API responses
  const { data: watchlistData, isSuccess: watchlistIsSuccess } = useGetWatchlistV2Query(
    { quote: "USD" },
    {
      // Skip if watchlist already has items (to avoid overwriting optimistic updates)
      skip: (currentWatchlist?.length ?? 0) > 0,
    },
  );

  //------------- VARIABLES -------------
  const classes = HeaderStyles();
  const classesMarkets = MarketTableStyles();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const history = useHistory();
  const currencyConfig = coinStatistics ? coinStatistics.currencyConfig : undefined;

  //------------- USE EFFECTS -------------
  useEffect(() => {
    // set loading state
    dispatchRedux(setUserRaf({ isLoadingRaf }));

    // set user Raf data
    if (rafData) {
      dispatchRedux(
        setUserRaf({
          tier: rafData.tier.toLowerCase() as RafTiers,
          referredAmount: rafData.referees,
          rafCode: rafData.referralCode,
          reward: rafData.referralProgram?.rewardPercent ?? "2",
          rafOptedIn: rafData.referralOptIn,
          OLD_RAF_SYSTEM: !["bronze", "silver", "gold", "black"].includes(rafData.tier),
        }),
      );
    }
  }, [rafData, isLoadingRaf]);

  useEffect(() => {
    if (data) {
      dispatchRedux(setMaintenanceStatus(data));
    }
  }, [data]);

  useEffect(() => {
    if (userData) {
      dispatchRedux(setAccountLock(userData.accountLock));
    }
  }, [userData]);

  // Sync watchlist query data to Redux on initial load only (when watchlist is empty)
  // This prevents overwriting optimistic updates or recent API responses
  useEffect(() => {
    if (
      watchlistIsSuccess &&
      watchlistData &&
      (!currentWatchlist || currentWatchlist.length === 0)
    ) {
      dispatchRedux(setWatchlist(watchlistData));
    }
  }, [watchlistIsSuccess, watchlistData, dispatchRedux, currentWatchlist]);

  //------------- FUNCTIONS -------------
  const handleBack = () => {
    history.goBack();
  };

  const handlePriceAlert = (configCode: string) => {
    dispatchRedux(togglePriceAlertModal());
    trackEvent(`Visit_Modal`, { modal: "Price_Alert_Model", configCode }, "Header");
    //resets RTKQ state
  };

  const handleInviteModalToggle = () => {
    setInviteModalOpen((prev) => !prev);
  };

  const handleRafCodeToggle = () => {
    history.push("/refer-a-friend");
  };

  return (
    <>
      {/* MOBILE */}
      {isMobile ? (
        <>
          <AppBar className={classes.appBar} position="static">
            <Grid container className={classes.container}>
              <Grid item>
                {back && (
                  <IconButton
                    size="large"
                    edge="start"
                    color="inherit"
                    aria-label="menu"
                    onClick={handleBack}>
                    <BackIcon />
                  </IconButton>
                )}
                {!back && (
                  <IconButton
                    id="mobileMenuButton"
                    size="large"
                    edge="start"
                    color="inherit"
                    aria-label="menu"
                    onClick={handleMenuOpen}>
                    <Hamburguer />
                  </IconButton>
                )}
              </Grid>
              <div style={{ width: "60%" }}>
                {title && <Typography className={classes.title}>{title}</Typography>}
                {!title && (
                  <NavLink to="/portfolio">
                    <CalebAndBrownLogo height={36} />
                  </NavLink>
                )}
              </div>
              <Box sx={{ display: "flex" }}>
                {hideFavourites && currencyConfig?.secondarySector !== "Stablecoin" && (
                  <Button
                    disableRipple
                    sx={{
                      "&:hover": {
                        background: "transparent",
                      },
                      marginTop: "6px",
                      minWidth: " 0px",
                      display: "flex",
                      paddingTop: "4px",
                      mr: 2,
                    }}
                    onClick={() => {
                      if (currencyConfig) {
                        handlePriceAlert(currencyConfig.code);
                      }
                    }}>
                    <NotificationAddIcon
                      className={classesMarkets.notificationIcon}
                      sx={{
                        width: "25px",
                        height: "25px",
                        paddingBottom: "2px",
                        color: theme.palette.primary.dark,
                      }}
                    />
                  </Button>
                )}
                {hideFavourites && (
                  <WatchToggle
                    height={"18px"}
                    width={"18px"}
                    code={currencyConfig?.code || ""}
                    variant={"icon"}
                  />
                )}
                {!hideRightIcon && !hideFavourites && (
                  <ProfileModal fullscreen={isMobile} ProfileInitialsScale="1.4" />
                )}
              </Box>
            </Grid>
          </AppBar>
        </>
      ) : (
        // DESKTOP
        <DesktopAppbar position="relative">
          <DesktopAppbarComponents>
            {/* Broker humanisation here */}
            <ProfileComponent setOpen={setOpen} text={"dark"} />
            {/* Collapsible searchbar here */}
            <CollapsibleGlobalSearchbar />

            <DesktopAppbarButtons>
              {/* RaF invite modal here */}
              {!isUkUser && !OLD_RAF_SYSTEM && rafOptedIn && !isUnverified ? (
                // if on RaF program and have opted in AND  is verified
                <ContainedButton
                  color="navy"
                  width="60px"
                  height="33px"
                  borderRadius="20px"
                  onClick={handleInviteModalToggle}>
                  Refer
                </ContainedButton>
              ) : !isUkUser && !OLD_RAF_SYSTEM && !rafOptedIn && !isUnverified ? (
                // if on RaF program and have not opted in AND is verified
                <ContainedButton
                  color="navy"
                  height="33px"
                  borderRadius="20px"
                  onClick={handleRafCodeToggle}>
                  Refer
                </ContainedButton>
              ) : (
                <></>
              )}
              {/* Profile Modal */}
              <ProfileModal fullscreen={isMobile} />
            </DesktopAppbarButtons>
          </DesktopAppbarComponents>
        </DesktopAppbar>
      )}
      {/* Broker humanisation modal */}
      <BrokerModal open={open} setOpen={setOpen} />
      {/* RaF Invite Modal*/}
      {!isUkUser && (
        <RaFInviteModal open={InviteModalOpen} onClose={handleInviteModalToggle} />
      )}
      {/* Logout to update Modal */}
      <LogoutToUpdateModal
        open={logoutToUpdateModal}
        onClose={() => setLogoutToUpdateModal(false)}
      />
    </>
  );
};

export default Header;
